/** Lignes et zones de la ZO telles qu'elles se montrent (docs/15 §4.1, §4.3) : celles du serveur, ou, pendant un
 *  geste du fuseau, un glissement de ligne ou des annulations en attente d'envoi, le calque recalculé côté client
 *  (`zoMeasures.relayout`) sur le repère de travail et les positions choisies de l'aperçu. Partagé par le calque
 *  (ZoMeasuresLayers), les poignées d'extrémité du mode édition (ManoeuvreLayers, useManoeuvreGestures) et les
 *  glissements (useMeasureGestures) : tous voient la même géométrie. Fonctions pures, plus un hook de lecture. */

import { useMemo } from "react";

import type { CorridorModel, Deployment, MeasureKey, MeasureLine, Measures, MeasureNames, Phase, PlacementMeasures } from "../../api/types";
import { type CorridorFrame, frameOf } from "./corridorFrame";
import { useGestureState } from "./gestureState";
import type { HandlePoint } from "./handles";
import { useManoeuvre } from "./useManoeuvre";
import { isMeasureLevel, linesAt, type MeasureLevel, NAMES, type Overrides, parseOverrides, relayout } from "./zoMeasures";

/** Niveau des lignes de la ZO du dispositif, ou null : CENTAC, pas de fuseau, pas de lignes calculées. */
export function measureLevelOf(deployment: Deployment | null | undefined): MeasureLevel | null {
  if (!deployment || deployment.centac || !deployment.corridor?.measures) return null;
  return isMeasureLevel(deployment.level) ? deployment.level : null;
}

/** Aperçus en cours (états partagés des gestes et de l'historique). */
export interface MeasurePreviews {
  /** Fuseau en cours de geste (`useManoeuvre.working`). */
  working: CorridorModel | null;
  /** Fuseau visé par des annulations en attente d'envoi. */
  historyPreview: CorridorModel | null;
  /** Positions choisies d'un glissement de ligne de la ZO (jusqu'à la réponse). */
  measurePreview: Overrides | null;
  /** Positions choisies visées par des annulations en attente d'envoi. */
  historyMeasures: { measures: PlacementMeasures | null } | null;
}

export interface MeasureView {
  level: MeasureLevel;
  /** Calque montré : celui du serveur, ou recalculé pour l'aperçu. */
  measures: Measures;
  /** Calque recalculé côté client (aperçu) : il suit le geste sans transition. */
  preview: boolean;
  /** Repère du modèle montré (fuseau de travail, sinon celui du serveur) ; null sans modèle. */
  frame: CorridorFrame | null;
}

function frameOrNull(model: CorridorModel | null): CorridorFrame | null {
  if (!model || model.left.length < 2 || model.left.length !== model.right.length) return null;
  try {
    return frameOf(model);
  } catch {
    return null;
  }
}

/** Calque montré pour un déploiement et des aperçus ; null sans lignes de la ZO (CENTAC, pas de fuseau). */
export function measureView(deployment: Deployment | null | undefined, previews: MeasurePreviews): MeasureView | null {
  const level = measureLevelOf(deployment);
  const server = deployment?.corridor?.measures;
  if (!level || !server) return null;
  const model = previews.working ?? previews.historyPreview ?? deployment?.corridor?.model ?? null;
  const frame = frameOrNull(model);
  const geometry = previews.working !== null || previews.historyPreview !== null;
  const overrides =
    previews.measurePreview ?? (previews.historyMeasures ? parseOverrides(previews.historyMeasures.measures, level) : null);
  if (!frame || (!geometry && !overrides)) return { level, measures: server, preview: false, frame };
  try {
    return { level, measures: relayout(server, level, frame, overrides ?? undefined), preview: true, frame };
  } catch {
    // Repère de travail dégénéré (paire confondue pendant un geste) : le calque du serveur reste affiché.
    return { level, measures: server, preview: false, frame };
  }
}

/** Aperçus lus dans les états partagés, hors rendu (gestes, poignées). */
export function currentPreviews(): MeasurePreviews {
  const gesture = useGestureState.getState();
  return {
    working: useManoeuvre.getState().working,
    historyPreview: gesture.historyPreview,
    measurePreview: gesture.measurePreview,
    historyMeasures: gesture.historyMeasures,
  };
}

/** Calque montré, recalculé quand le déploiement ou un aperçu change. */
export function useMeasureView(deployment: Deployment | null | undefined): MeasureView | null {
  const working = useManoeuvre((s) => s.working);
  const historyPreview = useGestureState((s) => s.historyPreview);
  const measurePreview = useGestureState((s) => s.measurePreview);
  const historyMeasures = useGestureState((s) => s.historyMeasures);
  return useMemo(
    () => measureView(deployment, { working, historyPreview, measurePreview, historyMeasures }),
    [deployment, working, historyPreview, measurePreview, historyMeasures],
  );
}

/** Poignées d'extrémité (`measureEnd`, mode édition) des lignes mobiles du temps affiché : une par limite, au premier
 *  point (limite gauche) et au dernier (limite droite) du tracé. LIMAR et LIMAV n'en ont pas : elles suivent la paire
 *  d'extrémité du fuseau. */
export function measureEndPoints(lines: readonly MeasureLine[], phase: Phase): HandlePoint[] {
  const out: HandlePoint[] = [];
  for (const line of linesAt(lines, phase)) {
    if (!line.movable || line.coordinates.length < 2) continue;
    const first = line.coordinates[0]!;
    const last = line.coordinates[line.coordinates.length - 1]!;
    out.push({ handle: { kind: "measureEnd", key: line.key, phase: line.phase, side: "left" }, at: [first[0], first[1]] });
    out.push({ handle: { kind: "measureEnd", key: line.key, phase: line.phase, side: "right" }, at: [last[0], last[1]] });
  }
  return out;
}

/** Noms d'une ligne ou d'une zone : ceux du calque, sinon ceux du catalogue du niveau. */
export function measureNames(view: Pick<MeasureView, "measures" | "level">, key: MeasureKey): MeasureNames | null {
  return view.measures.lines.find((l) => l.key === key)?.names ?? NAMES[view.level][key] ?? null;
}

/** Nom court d'une ligne dans la langue du dossier, suivi de son temps pour la FLOT et la CFL : « FLOT (T3) ». */
export function measureLabel(names: MeasureNames | null, key: MeasureKey, phase: Phase | null, language: "fr" | "en"): string {
  const short = names?.[language].short ?? key;
  return phase ? `${short} (${phase})` : short;
}
